/**
 * Egyptian colloquial copy for course / curriculum load failures.
 */

export type CourseLoadUiKind =
  | 'not_found'
  | 'load_failed'
  | 'network_error'
  | 'no_lessons'
  | 'unit_locked'
  | 'lesson_unavailable'
  | 'missing_id';

export interface CourseLoadUiResult {
  kind: CourseLoadUiKind;
  title: string;
  message: string;
  buttonLabel: string;
}

const normalize = (msg: string) => msg.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Map course-tree / curriculum load failures to Egyptian popup/page copy.
 */
export function mapCourseLoadError(raw?: string | null): CourseLoadUiResult {
  const msg = normalize(String(raw ?? ''));

  if (!msg || msg.includes('missing') || msg.includes('no course id')) {
    return {
      kind: 'missing_id',
      title: 'الكورس مش واضح 🤔',
      message: 'مفيش رقم كورس ظاهر في الرابط. ارجع لكورساتك وافتحه من هناك.',
      buttonLabel: 'روح لكورساتي',
    };
  }

  if (
    msg.includes('not found') ||
    msg.includes('غير موجودة') ||
    msg.includes('404')
  ) {
    return {
      kind: 'not_found',
      title: 'الكورس مش موجود 😕',
      message: 'الكورس اللي بتدور عليه مش ظاهر عندنا. يمكن اتشال أو الرابط غلط.',
      buttonLabel: 'روح لكورساتي',
    };
  }

  if (
    msg.includes('network') ||
    msg.includes('timeout') ||
    msg.includes('failed to fetch') ||
    msg.includes('حدث خطأ')
  ) {
    return {
      kind: 'network_error',
      title: 'النِت ضعيف شوية ⚠️',
      message: 'مقدرناش نحمّل الكورس دلوقتي. تأكد من الاتصال وحاول مرة كمان.',
      buttonLabel: 'حاول تاني',
    };
  }

  if (
    msg.includes('لا توجد وحدات') ||
    msg.includes('لا توجد دروس') ||
    msg.includes('no lesson') ||
    msg.includes('no unit') ||
    msg.includes('empty lessons')
  ) {
    return {
      kind: 'no_lessons',
      title: 'لسه مفيش دروس 📭',
      message:
        'الكورس والوحدات ظاهرين، بس المدرس لسه مضافش دروس جواهم. ارجع للمنهج وشوف الوحدات، وارجع تاني لما يتضاف المحتوى.',
      buttonLabel: 'ارجع للمنهج',
    };
  }

  if (
    msg.includes('ستصبح هذه الوحدة') ||
    msg.includes('انتهت فترة') ||
    msg.includes('غير متاحة') ||
    msg.includes('تاريخ التسجيل')
  ) {
    return {
      kind: 'unit_locked',
      title: 'الوحدة لسه مقفولة 🔒',
      message: raw?.trim() || 'الوحدة دي مش متاحة ليك دلوقتي. شوف المنهج وافتح المتاح.',
      buttonLabel: 'ارجع للمنهج',
    };
  }

  if (
    msg.includes('الدرس غير') ||
    msg.includes('فشل في تحميل تفاصيل الدرس') ||
    msg.includes('lesson')
  ) {
    return {
      kind: 'lesson_unavailable',
      title: 'الدرس مش متاح دلوقتي 😅',
      message: raw?.trim() || 'مقدرناش نفتح الدرس. ارجع للمنهج وجرّب درس تاني.',
      buttonLabel: 'ارجع للمنهج',
    };
  }

  if (msg.includes('فشل في تحميل') || msg.includes('failed to fetch course')) {
    return {
      kind: 'load_failed',
      title: 'مقدرناش نحمّل الكورس 😕',
      message: 'حاجة حصلت ومحتوى الكورس مجاش. حاول تاني بعد شوية.',
      buttonLabel: 'روح لكورساتي',
    };
  }

  return {
    kind: 'load_failed',
    title: 'مقدرناش نكمّل 😕',
    message: raw?.trim() || 'حاجة حصلت ومكملتش. ارجع لكورساتك وحاول من هناك.',
    buttonLabel: 'روح لكورساتي',
  };
}
